import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Snowflake, AlertTriangle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface FreezerOutTimeTimerProps {
  removedAt: Date | string;
  maxMinutes: number;
  materialName?: string;
  batchNumber?: string;
  onReturn?: () => Promise<void>;
  onExpired?: () => void;
  onWarning?: (minutesRemaining: number) => void;
  compact?: boolean;
  showReturnButton?: boolean;
}

export function FreezerOutTimeTimer({
  removedAt,
  maxMinutes,
  materialName,
  batchNumber,
  onReturn,
  onExpired,
  onWarning,
  compact = false,
  showReturnButton = true,
}: FreezerOutTimeTimerProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasWarned10Percent, setHasWarned10Percent] = useState(false);
  const [hasExpired, setHasExpired] = useState(false);

  const startDate = new Date(removedAt);
  const maxSeconds = maxMinutes * 60;

  useEffect(() => {
    const updateElapsed = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startDate.getTime()) / 1000);
      setElapsedSeconds(Math.max(0, elapsed));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startDate.getTime()]);

  const remainingSeconds = Math.max(0, maxSeconds - elapsedSeconds);
  const remainingMinutes = Math.ceil(remainingSeconds / 60);
  const percentUsed = Math.min(100, (elapsedSeconds / maxSeconds) * 100);
  const percentRemaining = 100 - percentUsed;
  const isExpired = remainingSeconds <= 0;

  // Alertas basadas en porcentaje
  useEffect(() => {
    if (percentRemaining <= 10 && !hasWarned10Percent && !isExpired) {
      setHasWarned10Percent(true);
      onWarning?.(remainingMinutes);
      toast.error(`⚠️ CRÍTICO: ${materialName || "Material"} - Solo ${remainingMinutes} min restantes`, {
        duration: 10000,
      });
    }

    if (isExpired && !hasExpired) {
      setHasExpired(true);
      onExpired?.();
      toast.error(`🔴 TIEMPO EXCEDIDO: ${materialName || "Material"} - ¡Guardar inmediatamente!`, {
        duration: 15000,
      });
    }
  }, [percentRemaining, isExpired, hasWarned10Percent, hasExpired]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins.toString().padStart(2, "0")}m`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleReturn = async () => {
    if (!onReturn) return;
    setLoading(true);
    try {
      await onReturn();
      toast.success("Material guardado en congelador");
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = () => {
    if (isExpired) return "bg-red-600";
    if (percentUsed >= 90) return "bg-red-500 animate-pulse";
    if (percentUsed >= 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getTextColor = () => {
    if (isExpired) return "text-red-600 font-bold";
    if (percentUsed >= 90) return "text-red-500 font-bold animate-pulse";
    if (percentUsed >= 70) return "text-yellow-600";
    return "text-green-600";
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Badge 
          variant="outline" 
          className={cn(
            "font-mono",
            isExpired && "bg-red-100 text-red-700 border-red-300",
            !isExpired && percentUsed >= 90 && "bg-red-50 text-red-600 border-red-200 animate-pulse",
            !isExpired && percentUsed >= 70 && percentUsed < 90 && "bg-yellow-50 text-yellow-700 border-yellow-300",
            !isExpired && percentUsed < 70 && "bg-blue-50 text-blue-700 border-blue-300"
          )}
        >
          <Snowflake className="w-3 h-3 mr-1" />
          {isExpired ? "EXCEDIDO" : formatTime(remainingSeconds)}
        </Badge>
        {showReturnButton && onReturn && (
          <Button size="sm" variant="outline" onClick={handleReturn} disabled={loading}>
            <Snowflake className="w-3 h-3 mr-1" />
            Guardar
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className={cn(
      "p-4 transition-all",
      isExpired && "border-red-500 bg-red-50 dark:bg-red-950/30",
      !isExpired && percentUsed >= 90 && "border-red-400 bg-red-50/50 dark:bg-red-950/20 animate-pulse",
      !isExpired && percentUsed >= 70 && percentUsed < 90 && "border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/20",
      !isExpired && percentUsed < 70 && "border-blue-300 bg-blue-50/30 dark:bg-blue-950/10"
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Snowflake className={cn("w-5 h-5", getTextColor())} />
          {materialName && <span className="font-medium">{materialName}</span>}
          {batchNumber && <Badge variant="outline" className="text-xs">{batchNumber}</Badge>}
        </div>
        {isExpired && (
          <Badge variant="destructive" className="animate-pulse">
            <AlertTriangle className="w-3 h-3 mr-1" />
            EXCEDIDO
          </Badge>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Tiempo restante:</span>
          <span className={cn("font-mono text-xl", getTextColor())}>
            {isExpired ? "00:00" : formatTime(remainingSeconds)}
          </span>
        </div>

        <Progress 
          value={100 - percentUsed} 
          className={cn("h-3", getProgressColor())}
        />

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Transcurrido: {formatTime(elapsedSeconds)}</span>
          <span>Máximo: {maxMinutes} min</span>
        </div>
      </div>

      {showReturnButton && onReturn && (
        <Button 
          onClick={handleReturn} 
          disabled={loading}
          className={cn(
            "w-full mt-4",
            isExpired ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
          )}
        >
          <Snowflake className="w-4 h-4 mr-2" />
          {loading ? "Guardando..." : "Guardar en Congelador"}
        </Button>
      )}
    </Card>
  );
}
